'use client';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { ChevronDown, Search, X } from 'lucide-react';
import { useState } from 'react';

interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectSectionProps {
  values: string[];
  onValuesChange: (values: string[]) => void;
  options: MultiSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  showScroll?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export function MultiSelectSection({
  values,
  onValuesChange,
  options,
  placeholder = '선택하세요',
  disabled = false,
  searchable = false,
  showScroll = false,
  size = 'md',
}: MultiSelectSectionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = searchable
    ? options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  const handleToggle = (value: string) => {
    const updatedValues = values.includes(value)
      ? values.filter(v => v !== value)
      : [...values, value];
    onValuesChange(updatedValues);
  };

  const handleRemove = (value: string) => {
    const updatedValues = values.filter(v => v !== value);
    onValuesChange(updatedValues);
  };

  const handleClearAll = () => {
    onValuesChange([]);
  };

  const selectedLabels = values.map(value => {
    const option = options.find(opt => opt.value === value);
    return option ? option.label : value;
  });

  return (
    <div className='w-full'>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant='outline'
            role='combobox'
            aria-expanded={isOpen}
            className={cn(
              'w-full justify-between text-left h-auto py-1 px-2',
              size === 'sm' && 'min-h-[2rem] text-sm',
              size === 'md' && 'min-h-[2.5rem] text-sm',
              size === 'lg' && 'min-h-[3rem] text-base'
            )}
            disabled={disabled}
          >
            <div className='flex flex-wrap gap-1'>
              {values.length === 0 ? (
                <span className='text-muted-foreground'>{placeholder}</span>
              ) : values.length <= 2 ? (
                selectedLabels.map((label, index) => (
                  <Badge
                    key={values[index]}
                    variant='secondary'
                    className='text-xs h-5'
                  >
                    {label}
                    <span
                      className='inline-flex items-center justify-center h-auto w-auto p-0 ml-1 cursor-pointer hover:opacity-70'
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemove(values[index]);
                      }}
                    >
                      <X className='h-3 w-3' />
                    </span>
                  </Badge>
                ))
              ) : (
                <Badge variant='secondary' className='text-xs h-5'>
                  {values.length}개 선택됨
                </Badge>
              )}
            </div>
            <ChevronDown className='h-4 w-4 opacity-50 shrink-0' />
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-full p-0' align='start'>
          <div className='p-3 space-y-2'>
            {searchable && (
              <div className='relative'>
                <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
                <Input
                  placeholder='검색...'
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className='pl-8'
                />
              </div>
            )}

            {values.length > 0 && (
              <div className='flex items-center justify-between pb-2 border-b'>
                <span className='text-sm text-muted-foreground'>
                  {values.length}개 선택됨
                </span>
                <span
                  onClick={handleClearAll}
                  className='h-auto py-1 px-2 text-xs cursor-pointer hover:bg-accent rounded text-muted-foreground hover:text-foreground'
                >
                  전체 해제
                </span>
              </div>
            )}

            <div
              className={`max-h-48 ${showScroll ? 'overflow-y-scroll' : 'overflow-y-auto'}`}
            >
              {filteredOptions.length === 0 ? (
                <div className='text-sm text-muted-foreground text-center py-2'>
                  {searchable && searchTerm
                    ? '검색 결과가 없습니다.'
                    : '옵션이 없습니다.'}
                </div>
              ) : (
                filteredOptions.map(option => (
                  <div
                    key={option.value}
                    className='flex items-center space-x-2 py-1.5 px-2 rounded cursor-pointer hover:bg-accent'
                    onClick={() => handleToggle(option.value)}
                  >
                    <Checkbox
                      checked={values.includes(option.value)}
                      onChange={() => handleToggle(option.value)}
                    />
                    <label className='text-sm flex-1 cursor-pointer'>
                      {option.label}
                    </label>
                  </div>
                ))
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
